import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityByKey, enumEntityModelCode } from '@data-layer/enums/entities';
import { TranslateService } from '@ngx-translate/core';
import { DESC_ENTITY } from '@constants/desc-entity';
import { enumIsfState } from '@data-layer/enums/isf';
import { IsfHelper, IsfSameAsParent } from '@helpers/isf.helper';

@Component({
  selector: 'desktop-isf-summary-shipping-party',
  template: `
    <desktop-isf-summary-card [header]="'LABELS.SHIPPING_PARTY' | translate">
      <ng-container body>
        <desktop-summary-entity
          [entity]="seller"
          [isShowIdentifier]="false"
          [entityCode]="enumEntityModelCode.SELLER"
          [isEditedRecently]="isSellerEdited"
          [isEditMode]="isEditMode"
          [isShowEncircledButton]="true"
          [sameAs]="isSameAs(seller, enumEntityModelCode.SELLER)"
          (edit)="edit.emit({ id: seller?.id, entityType: enumEntityModelCode.SELLER })"
        ></desktop-summary-entity>

        <desktop-summary-entity
          [entity]="consolidator"
          [isShowIdentifier]="false"
          [entityCode]="enumEntityModelCode.CONSOLIDATOR"
          [isEditedRecently]="isConsolidatorEdited"
          [isEditMode]="!!consolidator ? isEditMode : isEntityEditable"
          [isShowEncircledButton]="true"
          [canBeSubmitted]="true"
          [sameAs]="isSameAs(consolidator, enumEntityModelCode.CONSOLIDATOR)"
          (edit)="edit.emit({ id: consolidator?.id, entityType: enumEntityModelCode.CONSOLIDATOR })"
        ></desktop-summary-entity>

        <desktop-summary-entity
          [entity]="staffingLocation"
          [isShowIdentifier]="false"
          [entityCode]="enumEntityModelCode.STAFFING_LOCATION"
          [isEditedRecently]="isStaffingLocationEdited"
          [isEditMode]="!!staffingLocation ? isEditMode : isEntityEditable"
          [isShowEncircledButton]="true"
          [canBeSubmitted]="true"
          [sameAs]="isSameAs(staffingLocation, enumEntityModelCode.STAFFING_LOCATION)"
          (edit)="edit.emit({ id: staffingLocation?.id, entityType: enumEntityModelCode.STAFFING_LOCATION })"
        ></desktop-summary-entity>
      </ng-container>
    </desktop-isf-summary-card>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopSummaryShippingPartyComponent {
  @Input() public importer: EntityModel;
  @Input() public seller: EntityModel;
  @Input() public consolidator: EntityModel;
  @Input() public staffingLocation: EntityModel;
  @Input() public isSellerEdited: boolean;
  @Input() public isConsolidatorEdited: boolean;
  @Input() public isStaffingLocationEdited: boolean;
  @Input() public isPaymentPending: boolean;
  @Input() public isEditMode: boolean;
  @Input() public isfState: enumIsfState;
  @Output() public edit: EventEmitter<{ id: number; entityType: enumEntityModelCode }> = new EventEmitter<{
    id: number;
    entityType: enumEntityModelCode;
  }>();

  public enumEntityModelCode = enumEntityModelCode;
  public enumEntityByKey = enumEntityByKey;

  constructor(private translateService: TranslateService) {}

  // #13536: "Same as <party>" is decided by identity with the preceding party (same entity row), not by entityCode
  public isSameAs(entity: EntityModel, entityCode: enumEntityModelCode): string {
    const parentCode: enumEntityModelCode = IsfHelper.getSameAsParentCode(entity, this.getSameAsParents(entityCode));
    if (parentCode === null) {
      return;
    }

    const entityName: string = this.translateService.instant(DESC_ENTITY[parentCode].titleDesktop);
    return this.translateService.instant('LABELS.SAME_AS', { entityName });
  }

  public get isEntityEditable(): boolean {
    if (this.isPaymentPending) {
      return false;
    }

    switch (this.isfState) {
      case enumIsfState.NOT_SENT:
      case enumIsfState.NOT_VALID_AT_PROVIDER:
      case enumIsfState.NO_BILL_ON_FILE:
      case enumIsfState.BILL_TYPE_NOT_MATCHED:
      case enumIsfState.APPROVED:
      case enumIsfState.REJECTED_CBP:
        return true;
      default:
        return false;
    }
  }

  private getSameAsParents(entityCode: enumEntityModelCode): IsfSameAsParent[] {
    const parents: IsfSameAsParent[] = [
      { code: enumEntityModelCode.IMPORTER, entity: this.importer },
      { code: enumEntityModelCode.SELLER, entity: this.seller },
      { code: enumEntityModelCode.CONSOLIDATOR, entity: this.consolidator },
    ];
    const slotIndex: number = parents.findIndex((parent: IsfSameAsParent) => parent.code === entityCode);

    return slotIndex === -1 ? parents : parents.slice(0, slotIndex);
  }
}
